import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Trash2,
  Loader2,
  GraduationCap,
  Calendar,
  Mail,
  User,
  Download,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  positionRequestService,
  UserPositionRequestDTO,
} from "@/services/positionRequestService";
import { format, parseISO } from "date-fns";
import { axiosInstance } from "@/pages/auth/auth.context";

export default function ManagePositionRequests() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] =
    useState<UserPositionRequestDTO | null>(null);

  const {
    data: positionRequests = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["position-requests", "admin"],
    queryFn: positionRequestService.getAllPositionRequests,
    meta: {
      onError: (error: any) => {
        toast({
          title: "Error loading position requests",
          description: error.message || "Failed to load position requests",
          variant: "destructive",
        });
      },
    },
  });

  const deleteMutation = useMutation({
    mutationFn: positionRequestService.deletePositionRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["position-requests", "admin"],
      });
      toast({
        title: "Position request deleted",
        description: "The position request has been removed successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting request",
        description: error.message || "Failed to delete position request",
        variant: "destructive",
      });
    },
  });

  const exportMutation = useMutation({
    mutationFn: async () => {
      const response = await axiosInstance.get(
        "/reports/position-learning-requests/export",
        {
          responseType: "blob",
        },
      );
      return response;
    },
    onSuccess: (response) => {
      // Create blob URL and download file
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers["content-disposition"];
      let filename = "Position_Learning_Requests_Report.xlsx";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Export successful",
        description: "Position learning requests report has been downloaded",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Export failed",
        description:
          error.message || "Failed to export position learning requests",
        variant: "destructive",
      });
    },
  });

  const handleDelete = (request: UserPositionRequestDTO) => {
    setRequestToDelete(request);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (requestToDelete) {
      deleteMutation.mutate(requestToDelete.id);
      setDeleteDialogOpen(false);
      setRequestToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setRequestToDelete(null);
  };

  const handleExport = () => {
    exportMutation.mutate();
  };

  const sortedRequests = [...positionRequests].sort((a, b) => {
    // Sort by creation date (newest first)
    return (
      new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
    );
  });

  const groupedRequests = sortedRequests.reduce(
    (groups, request) => {
      const positionKey = request.positionTableName;
      if (!groups[positionKey]) {
        groups[positionKey] = [];
      }
      groups[positionKey].push(request);
      return groups;
    },
    {} as Record<string, UserPositionRequestDTO[]>,
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <GraduationCap className="h-8 w-8 text-foreground" />
          <h1 className="text-3xl font-bold text-foreground">
            Position Requests
          </h1>
        </div>
        <p className="text-foreground">
          Manage employee position learning requests and track training progress
        </p>
      </div>

      <Card className="shadow-md border-border">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-foreground">
              <Calendar className="h-5 w-5" />
              All Position Requests
            </span>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={handleExport}
                disabled={
                  exportMutation.isPending || positionRequests.length === 0
                }
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
                size="sm"
              >
                {exportMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export to Excel
              </Button>
              <Badge variant="secondary" className="text-sm text-foreground">
                {positionRequests.length} total request
                {positionRequests.length !== 1 ? "s" : ""}
              </Badge>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin text-foreground mx-auto mb-3" />
                <p className="text-foreground">Loading position requests...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-6 max-w-md mx-auto">
                <p className="text-destructive font-medium mb-2">
                  Error loading requests
                </p>
                <p className="text-destructive text-sm">
                  Please refresh the page or contact support if the problem
                  persists.
                </p>
              </div>
            </div>
          ) : sortedRequests.length === 0 ? (
            <div className="text-center py-12">
              <GraduationCap className="h-16 w-16 mx-auto mb-4 text-foreground" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                No position requests
              </h3>
              <p className="text-foreground">
                No employees have requested position training yet.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedRequests).map(
                ([positionName, requests]) => (
                  <div
                    key={positionName}
                    className="border border-border rounded-lg"
                  >
                    {/* Position Header */}
                    <div className="border-b border-border px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-full">
                            <GraduationCap className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-foreground">
                              {positionName}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {requests.length} employee
                              {requests.length !== 1 ? "s" : ""} requesting
                              training
                            </p>
                          </div>
                        </div>
                        <Badge
                          variant="secondary"
                          className="bg-primary/10 text-muted-foreground"
                        >
                          {requests.length}
                        </Badge>
                      </div>
                    </div>

                    {/* Employees List */}
                    <div className="divide-y border-border">
                      {requests.map((request) => (
                        <div
                          key={request.id}
                          className="flex items-center justify-between px-6 py-4"
                        >
                          <div className="flex items-center gap-4">
                            <div className="bg-primary/10 p-2 rounded-full flex-shrink-0">
                              <User className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div>
                              <h4 className="font-medium text-foreground">
                                {request.userName}
                              </h4>
                              <div className="flex items-center gap-1 text-sm text-foreground">
                                <Mail className="h-3 w-3" />
                                {request.userEmail}
                              </div>
                            </div>
                          </div>

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(request)}
                            disabled={deleteMutation.isPending}
                            className="text-destructive hover:text-destructive hover:bg-destructive/90 border-destructive hover:border-destructive/30"
                          >
                            {deleteMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                            <span className="hidden sm:inline ml-2">
                              Delete
                            </span>
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ),
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">
              Delete Position Request
            </AlertDialogTitle>
            <AlertDialogDescription className="text-foreground">
              {requestToDelete && (
                <>
                  Are you sure you want to delete the position request for{" "}
                  <span className="font-medium text-foreground">
                    {requestToDelete.userName}
                  </span>{" "}
                  -{" "}
                  <span className="font-medium text-foreground">
                    {requestToDelete.positionTableName}
                  </span>
                  ?
                  <br />
                  <br />
                  This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleCancelDelete}
              className="border-border text-foreground hover:bg-accent hover:text-accent-foreground"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
